import ApplicationTracker from "./ApplicationTracker";
import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { useLocation } from "react-router-dom";
import {
  isAddressSectionActive,
  isApplicationCreated,
  isCoapplicantSectionActive,
  isCredentialsSectionActive,
  isFinalizeAccountActive,
  isHomeDetailsSectionActive,
  isLegalSectionActive,
  isMortgageSectionActive,
  isProfileSectionActive,
  isVerifyIdentitySectionActive,
} from "@/lib/util";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store/dtm";
import { createApiClient } from "@/lib/Requests";
import { FundmoreFields, TokenData } from "@/lib/DTMWebAPIClient.generated";
import { useAuth } from "@/context/AuthContext";
import { Sidebar } from 'primereact/sidebar';
import { IoMdSettings } from "react-icons/io";
import { Toast } from 'primereact/toast';
import { InputSwitch } from "primereact/inputswitch";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { selectFundmoreSSO } from "@/slices/sessionDataSlice";
import dayjs from "dayjs";

export default function NavBar(props: any) {
  var diagnosticsEnabled: Boolean = (document.querySelector('meta[data-enable-diagnostics]') as HTMLMetaElement)?.dataset?.enableDiagnostics == "true";
  const credential = useSelector(
    (state: RootState) => state.credential.credential
  );
  const client = createApiClient({ timeout: 60000, token: credential.accessToken });
  const [mobileMenuShown, setmobileMenuShown] = useState(false);
  const [displayDiagnostics, setDisplayDiagnostics] = useState(false);
  const [signoutInProgress, setSignoutInProgress] = useState(false);
  const [quickIdle, setQuickIdle] = useState(sessionStorage.getItem("quickIdle") == "true" ? true : false);

  const [applicantAPIStatus, setApplicantAPIStatus] = useState(0);
  const [dealAPIStatus, setDealAPIStatus] = useState(0);
  const [IRSStatus, setIRSStatus] = useState(0);
  const [adminAPIStatus, setAdminAPIStatus] = useState(0);
  const [robStatus, setRobStatus] = useState(0);
  const [fetchingLink, setFetchingLink] = useState(false);

  const pathname = useLocation().pathname.toLowerCase();
  const buildInfoTag = (document.querySelector('meta[data-version]') as HTMLElement);
  const buildNumber = buildInfoTag?.dataset?.version;
  const identityVerification = buildInfoTag?.dataset?.enableAlloy == "true" ? "Enabled" : "Disabled";
  const phoneVerification = buildInfoTag?.dataset?.enableTwilio == "true" ? "Enabled" : "Disabled";
  const tmxId = buildInfoTag?.dataset?.tmxOrgid;
  const tmxStatus = buildInfoTag?.dataset?.enableTmx == "true" ? "Enabled" : "Disabled";

  const credentialsSectionActive = isCredentialsSectionActive(pathname);
  const mortgageSectionActive = isMortgageSectionActive(pathname);
  const profileSectionActive = isProfileSectionActive(pathname);
  const addressSectionActive = isAddressSectionActive(pathname);
  const homeSectionActive = isHomeDetailsSectionActive(pathname);
  const finalizeAccountSectionActive = isFinalizeAccountActive(pathname);
  const coapplicantSectionActive = isCoapplicantSectionActive(pathname);
  const legalTermsSectionActive = isLegalSectionActive(pathname);
  const verifyIdentitySectionActive = isVerifyIdentitySectionActive(pathname);
  const applicationCreated = isApplicationCreated(pathname);
  const credentialsCreated = useSelector(
    (state: RootState) => state.applicant.applicant.credentialsCreated
  );
  const applicant = useSelector(
    (state: RootState) => state.applicant.applicant
  );
  const application = useSelector(
    (state: RootState) => state.application.application
  );
  const sessionData = useSelector(selectFundmoreSSO);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { logout } = useAuth();
  const versionToast = useRef<Toast>(null);

  const toggleMobileMenuVisiblity = () => {
    setmobileMenuShown(mobileMenuShown ? false : true);
  };

  const handleClick = () => {
    if (credentialsCreated) {
      //Sign out and return to login page
      if (signoutInProgress) {
        return;
      }
      setSignoutInProgress(true);
      client.apiAuthLogout(new TokenData({
        accessToken: credential.accessToken,
        refreshToken: credential.refreshToken,
      })).then(function (result) {
        setSignoutInProgress(false);
        logout();
        navigate("/login", { replace: true });
      }).catch((error) => {
        console.log(error);
      });
    } else {
      navigate("/login");
    }
  }

  const navigateHome = () => {
    navigate("/home", { replace: true });
  }

  const copyVersionNumberToClipboard = () => {
    navigator.clipboard.writeText(buildNumber ? buildNumber : "");
    versionToast.current?.show({ severity: 'success', summary: 'Copied ' + buildNumber + ' to clipboard.', life: 1000 });
  }

  const toggleIdleSetting = (quickIdle: boolean) => {
    setQuickIdle(quickIdle);
    if (quickIdle) {
      sessionStorage.setItem("quickIdle", "true");
    } else {
      sessionStorage.removeItem("quickIdle");
    }
    const timeout = setTimeout(() => {
      handleClick();
    }, 750)
    return () => clearTimeout(timeout)
  }

  useEffect(() => {
    if (displayDiagnostics) {
      setApplicantAPIStatus(0);
      setDealAPIStatus(0);
      setIRSStatus(0);
      setAdminAPIStatus(0);
      setRobStatus(0);
      const timeout = setTimeout(() => {
        client.apiHeartbeatApplicantmanagement().then(async result => {
          if (result && result.status == "Healthy") {
            setApplicantAPIStatus(1);
          } else {
            setApplicantAPIStatus(2);
          }
        }).catch((error) => {
          setApplicantAPIStatus(2);
        });
        client.apiHeartbeatMortgagedeals().then(async result => {
          if (result && result.status == "Healthy") {
            setDealAPIStatus(1);
          } else {
            setDealAPIStatus(2);
          }
        }).catch((error) => {
          setDealAPIStatus(2);
        });
        client.apiHeartbeatIrs().then(async result => {
          if (result && result.timestamp) {
            setIRSStatus(1);
          } else {
            setIRSStatus(2);
          }
        }).catch((error) => {
          setIRSStatus(2);
        });
        client.apiHeartbeatAdmin().then(async result => {
          if (result && result.status == "Healthy") {
            setAdminAPIStatus(1);
          } else {
            setAdminAPIStatus(2);
          }
        }).catch((error) => {
          setAdminAPIStatus(2);
        });
        client.apiHeartbeatRob().then(async result => {
          if (result && result.status == "Healthy") {
            setRobStatus(1);
          } else {
            setRobStatus(2);
          }
        }).catch((error) => {
          setRobStatus(2);
        });
      }, 1000)
      return () => clearTimeout(timeout)
    }
  }, [displayDiagnostics]);

  const fetchDocIQLink = () => {
    // Fetch the Document IQ link
    setFetchingLink(true);

    const emailStr = (credential.email ?? '').trim();
    const emailIsNumber = /^[0-9]+$/.test(emailStr);
    const rawPhoneNumber = (applicant.phoneNumber || '').replace(/\D/g, '');

    client.dociqRedirect(new FundmoreFields({
      fm_application_id: applicant.fmApplicationId,
      fm_applicant_id: applicant.fmApplicantId,
      mcu_applicant_id: applicant.id.toString(),
      deal_id: application.id,
      cust_no: emailIsNumber ? +emailStr : applicant.custno,
      first_name: applicant.firstName,
      last_name: applicant.lastName,
      dob: dayjs(applicant.dob_year + '-' + applicant.dob_month + '-' + applicant.dob_day),
      phone: rawPhoneNumber,
      email: emailIsNumber ? applicant.email : credential.email,
    })).then(function (result) {
      setFetchingLink(false);
      if (result) {
      window.open(result, "_blank");
      }
    }).catch((error) => {
      setFetchingLink(false);
      console.error(error);
    });

  }

  return (
    <>
      <nav id="topbar" className="md:ml-18" style={{ width: screen.width }}>
        <div className="grid grid-cols-4 grid-flow-col gap-4 mt-2">
          <div className="sm:hidden flex">
            <button
              className={clsx("outline-none menu-button ml-5")}
              onClick={toggleMobileMenuVisiblity}
            >
              <span className={clsx(mobileMenuShown || props.hideMobileMenu ? "hidden" : "")}>
                <img
                  src="assets/mobile-menu-button.svg"
                  alt="Mobile menu button"
                  width={18}
                  height={12}
                ></img>
              </span>
              <span className={clsx(mobileMenuShown ? "" : "hidden")}>
                <img
                  src="assets/cross-icon.svg"
                  alt="Mobile menu button"
                  width={18}
                  height={12}
                ></img>
              </span>
            </button>
          </div>
          <div className="mt-2 max-md:col-span-3 col-span-1 col-1 ml-5">
            <img
              src="assets/Meridian_Logo_Blue.svg"
              alt="Meridian"
              width={150}
              height={25}
            />
          </div>
          <div>
            <div className="max-sm:hidden absolute mt-3 left-[343px] max-md:left-[280px] ">
              <a href="tel:1-866.592.2226" target="_blank" aria-label="Questions? Call 1-866.592.2226">
                <p className="active ">{applicationCreated || credentialsCreated ? '' : 'Questions? Call 1-866.592.2226'}</p>
              </a>
              {verifyIdentitySectionActive && <a onClick={() => { navigate("/home") }}>
                <p className="active underline cursor-pointer">Go to Dashboard</p>
              </a>}
            </div>
            <div className="max-sm:hidden absolute mt-3 right-32 max-lg:right-0 max-md:right-3 flex" style={{ maxHeight: 32 }}>
              {(applicationCreated || credentialsCreated) &&
                <>
                  <div className="mr-3">
                    <a>
                      <p className="active cursor-pointer" onClick={() => { navigateHome() }} >Home</p>
                    </a>
                  </div>
                  {applicant.fmApplicationId != "" && applicant.fmApplicantId != "" &&
                    (credential.email != "" || applicant.email != "") &&
                    <div className="mr-3">
                      <a>
                        <p className="active cursor-pointer" onClick={() => { fetchDocIQLink() }} >
                          Documents
                          <FontAwesomeIcon
                            icon={faSpinner}
                            spin
                            style={{ marginRight: 8 }}
                            className={clsx(fetchingLink ? "ml-2" : "hidden")}
                          />
                        </p>
                      </a>
                    </div>
                  }
                  {/* <div className="mr-3">
                <a>
                  <p className="active">Message Centre</p>
                </a>
              </div> */}
                </>
              }
              {(sessionData == undefined || sessionData.request === "" || sessionData.request === undefined) &&
                <a className="mr-3" onClick={() => { handleClick() }} style={{
                  paddingLeft: 12, borderLeft:
                    (applicationCreated || credentialsCreated) ? "2px solid var(--brand-slate-light)" : ""
                }}>
                  {!signoutInProgress && (<p className="active cursor-pointer">{applicationCreated || credentialsCreated ? 'Sign Out' : 'I’m already a member'}</p>)}
                  {signoutInProgress && (<p className="active">{applicationCreated || credentialsCreated ? 'Signing Out...' : 'I’m already a member'}</p>)}
                </a>
              }
              {diagnosticsEnabled && <IoMdSettings className="cursor-pointer" style={{ fontSize: 28, color: 'var(--brand-blue)' }} onClick={() => { setDisplayDiagnostics(true) }} />}
            </div>
          </div>
        </div>
        <hr />

        {credentialsSectionActive && (
          <div
            className={clsx(
              "sm:hidden small-tracker inline-flex items-center justify-between w-full"
            )}
          >
            <p className="brand-blue-text">Set up your credentials</p>
            <div className="small-tracker-label">
              <span className="brand-blue-text absolute">STEP 01/{applicant.isProspect ? "07" : "05"}</span>
            </div>
          </div>
        )}
        {mortgageSectionActive && (
          <div
            className={clsx(
              "sm:hidden small-tracker inline-flex items-center justify-between w-full"
            )}
          >
            <p className="brand-blue-text">Add your mortgage details</p>
            <div className="small-tracker-label">
              <span className="brand-blue-text absolute">STEP 02/{applicant.isProspect ? "07" : "05"}</span>
            </div>
          </div>
        )}
        {profileSectionActive && (
          <div
            className={clsx(
              "sm:hidden small-tracker inline-flex items-center justify-between w-full"
            )}
          >
            <p className="brand-blue-text">Build your profile</p>
            <div className="small-tracker-label">
              <span className="brand-blue-text absolute">STEP 03/{applicant.isProspect ? "07" : "05"}</span>
            </div>
          </div>
        )}
        {homeSectionActive && (
          <div
            className={clsx(
              "sm:hidden small-tracker inline-flex items-center justify-between w-full"
            )}
          >
            <p className="brand-blue-text">Add the details of your home</p>
            <div className="small-tracker-label">
              <span className="brand-blue-text absolute">STEP 04/{applicant.isProspect ? "07" : "05"}</span>
            </div>
          </div>
        )}
        {finalizeAccountSectionActive && (
          <div
            className={clsx(
              "sm:hidden small-tracker inline-flex items-center justify-between w-full"
            )}
          >
            <p className="brand-blue-text">Review your account</p>
            <div className="small-tracker-label">
              <span className="brand-blue-text absolute">STEP 05/{applicant.isProspect ? "07" : "05"}</span>
            </div>
          </div>
        )}
        {legalTermsSectionActive && (
          <div
            className={clsx(
              "sm:hidden small-tracker inline-flex items-center justify-between w-full"
            )}
          >
            <p className="brand-blue-text">Review legal terms</p>
            <div className="small-tracker-label">
              <span className="brand-blue-text absolute">STEP 06/07</span>
            </div>
          </div>
        )}
        {verifyIdentitySectionActive && (
          <div
            className={clsx(
              "sm:hidden small-tracker inline-flex items-center justify-between w-full"
            )}
          >
            <p className="brand-blue-text">Verify your identity</p>
            <div className="small-tracker-label">
              <span className="brand-blue-text absolute">STEP 07/07</span>
            </div>
          </div>
        )}
      </nav>
      <div className="sm:hidden">
        <div
          className={clsx(
            mobileMenuShown ? " " : "hidden",
            "small-sidebar flex justify-center max-xxs:w-full"
          )}
        >
          <ApplicationTracker />
          <div className="absolute bottom-20 px-2">
            <a href="tel:1-866.592.2226" target="_blank" aria-label="Questions? Call 1-866.592.2226">
              <p className="active underline"></p>
              <p className="active mb-5 ml-3">{applicationCreated ? '' : 'Questions? Call 1-866.592.2226'}</p>
            </a>
            {credentialsCreated &&
              <a onClick={() => { handleClick() }}>
                <button
                  type="submit"
                  className="secondary w-full sm:w-28 h-11 mb-5"
                >
                  I’m already a member
                </button>
              </a>
            }
            {!credentialsCreated &&
              <a>
                <button
                  type="submit"
                  className="secondary w-full sm:w-28 h-11 mb-5"
                >
                  Sign out
                </button>
              </a>
            }
            <div
              className="ml-4"
              role="region"
              aria-label="Side Navigation Footer"
            >
              <p className="text-sm ml-2">
                <a target="_blank">Privacy</a>
                &nbsp;|&nbsp;
                <a target="_blank">Terms & Conditions</a>
              </p>
              <p className="text-sm ml-2">&copy; 2025 Meridian Credit Union.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="sm:hidden">
        <div className={clsx(mobileMenuShown ? "active" : "", "overlay")}></div>
      </div>
      <Toast ref={versionToast} />
      <Sidebar header={<h2 style={{ marginBottom: 0 }}>DTM Diagnostics</h2>} visible={displayDiagnostics} position="right" onHide={() => setDisplayDiagnostics(false)}>
        <p className="sub-heading mt-5 cursor-pointer" style={{ fontSize: 16 }} onClick={() => { copyVersionNumberToClipboard() }}><b>Build Number: </b>{buildNumber}</p>
        <p className="sub-heading mt-5" style={{ fontSize: 16 }}><b>ID Verification: </b>{identityVerification}</p>
        <p className="sub-heading" style={{ fontSize: 16 }}><b>Twilio Phone Verification: </b>{phoneVerification}</p>
        <p className="sub-heading mt-5" style={{ fontSize: 16 }}><b>ThreatMatrix: </b>{tmxStatus}</p>
        <p className="sub-heading" style={{ fontSize: 16 }}><b>ThreatMatrix ID: </b>{tmxId}</p>
        <div className="flex mt-5">
          <p className="sub-heading"><b>Enable 30 Second Idle</b></p>
          <InputSwitch checked={quickIdle} onChange={(e) => toggleIdleSetting(e.value)} className="ml-3" />
        </div>

        <p className="sub-heading mt-5 flex flex-row justify-between" style={{ fontSize: 16 }}><b>Applicant API Status: </b>
          <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(applicantAPIStatus == 0 ? "" : "hidden")} />
          <img src={applicantAPIStatus == 1 ? "assets/completed.svg" : "assets/warning-icon.svg"} className={clsx(applicantAPIStatus != 0 ? "mr-1 ml-3" : "hidden")} alt="complete" />
        </p>
        <p className="sub-heading mt-2 flex flex-row justify-between" style={{ fontSize: 16 }}><b>Mortgage Deals API Status: </b>
          <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(dealAPIStatus == 0 ? "" : "hidden")} />
          <img src={dealAPIStatus == 1 ? "assets/completed.svg" : "assets/warning-icon.svg"} className={clsx(dealAPIStatus != 0 ? "mr-1 ml-3" : "hidden")} alt="complete" />
        </p>
        <p className="sub-heading mt-2 flex flex-row justify-between" style={{ fontSize: 16 }}><b>Identity Request Service Status: </b>
          <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(IRSStatus == 0 ? "" : "hidden")} />
          <img src={IRSStatus == 1 ? "assets/completed.svg" : "assets/warning-icon.svg"} className={clsx(IRSStatus != 0 ? "mr-1 ml-3" : "hidden")} alt="complete" />
        </p>
        <p className="sub-heading mt-2 flex flex-row justify-between" style={{ fontSize: 16 }}><b>Admin API Status: </b>
          <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(adminAPIStatus == 0 ? "" : "hidden")} />
          <img src={adminAPIStatus == 1 ? "assets/completed.svg" : "assets/warning-icon.svg"} className={clsx(adminAPIStatus != 0 ? "mr-1 ml-3" : "hidden")} alt="complete" />
        </p>
        <p className="sub-heading mt-2 flex flex-row justify-between" style={{ fontSize: 16 }}><b>ROB Support Service Status: </b>
          <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(robStatus == 0 ? "" : "hidden")} />
          <img src={robStatus == 1 ? "assets/completed.svg" : "assets/warning-icon.svg"} className={clsx(robStatus != 0 ? "mr-1 ml-3" : "hidden")} alt="complete" />
        </p>
      </Sidebar>
    </>
  );
}
